import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../contexts/NotificationContext';
import { apiService } from '../services/api';
import Button from './ui/Button';
import Card from './ui/Card';
import './NotificationSettings.css';

const NotificationSettings: React.FC = () => {
  const { t } = useTranslation();
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    testNotification,
    clearError,
    canSubscribe,
  } = useNotifications();

  const [isTriggeringScheduled, setIsTriggeringScheduled] = React.useState(false);
  const [isTestingServiceWorker, setIsTestingServiceWorker] = React.useState(false);

  const handleTriggerScheduled = async () => {
    setIsTriggeringScheduled(true);
    try {
      // Use the API service instead of direct fetch
      const response = await apiService.notifications.triggerScheduled();
      
      if (response.data.success) {
        const result = response.data;
        console.log('Scheduled notifications triggered:', result);
        alert(t('notifications:settings.messages.scheduledTriggered'));
      } else {
        throw new Error(t('notifications:settings.errors.failedToTrigger'));
      }
    } catch (error) {
      console.error('Error triggering scheduled notifications:', error);
      alert(t('notifications:settings.errors.failedToTrigger'));
    } finally {
      setIsTriggeringScheduled(false);
    }
  };

  const handleTestServiceWorker = async () => {
    setIsTestingServiceWorker(true);
    try {
      // Test if service worker can show notifications directly
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.active) {
          // Send a test message to the service worker
          registration.active.postMessage({ type: 'TEST_NOTIFICATION' });
          console.log('🧪 Test notification message sent to service worker');
          alert(t('notifications:settings.messages.testSentToServiceWorker'));
        } else {
          throw new Error(t('notifications:settings.errors.serviceWorkerNotActive'));
        }
      } else {
        throw new Error(t('notifications:settings.errors.serviceWorkerNotSupported'));
      }
    } catch (error: any) {
      console.error('Error testing service worker:', error);
      alert(t('notifications:settings.errors.serviceWorkerTestFailed', { message: error.message }));
    } finally {
      setIsTestingServiceWorker(false);
    }
  };

  const getPermissionStatusText = () => {
    switch (permission) {
      case 'granted':
        return t('notifications:settings.permissionStatus.allowed');
      case 'denied':
        return t('notifications:settings.permissionStatus.blocked');
      case 'default':
        return t('notifications:settings.permissionStatus.notSet');
      default:
        return t('notifications:settings.permissionStatus.unknown');
    }
  };

  const getPermissionStatusColor = () => {
    switch (permission) {
      case 'granted':
        return 'success';
      case 'denied':
        return 'error';
      case 'default':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <div className="notification-settings">
          <h3>{t('notifications:settings.title')}</h3>
          <div className="notification-status">
            <p className="status-message error">
              {t('notifications:settings.errors.notSupported')}
            </p>
            <p>
              {t('notifications:settings.messages.browserNotSupported')}
            </p>
            <ul>
              <li>{t('notifications:settings.messages.updateBrowser')}</li>
              <li>{t('notifications:settings.messages.useDifferentBrowser')}</li>
              <li>{t('notifications:settings.messages.enableJavaScript')}</li>
            </ul>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="notification-settings">
        <h3>{t('notifications:settings.title')}</h3>
        
        {/* Error Display */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <Button onClick={clearError} variant="secondary" size="sm">
              {t('notifications:settings.actions.dismiss')}
            </Button>
          </div>
        )}

        {/* Permission Status */}
        <div className="notification-status">
          <h4>{t('notifications:settings.permissionStatus.title')}</h4>
          <div className={`status-badge ${getPermissionStatusColor()}`}>
            {getPermissionStatusText()}
          </div>
          <p>
            {t('notifications:settings.permissionStatus.current')}: <strong>{permission}</strong>
          </p>
        </div>

        {/* Subscription Status */}
        <div className="notification-status">
          <h4>{t('notifications:settings.subscriptionStatus.title')}</h4>
          <div className={`status-badge ${isSubscribed ? 'success' : 'warning'}`}>
            {isSubscribed ? t('notifications:settings.subscriptionStatus.subscribed') : t('notifications:settings.subscriptionStatus.notSubscribed')}
          </div>
          <p>
            {isSubscribed 
              ? t('notifications:settings.messages.currentlyReceiving')
              : t('notifications:settings.messages.notSubscribed')
            }
          </p>
        </div>

        {/* Action Buttons */}
        <div className="notification-actions">
          {permission === 'default' && (
            <Button 
              onClick={requestPermission} 
              disabled={isLoading}
              variant="primary"
              fullWidth
            >
              {isLoading ? t('notifications:settings.actions.requesting') : t('notifications:settings.actions.enableNotifications')}
            </Button>
          )}

          {permission === 'denied' && (
            <div className="permission-denied">
              <p className="warning-message">
                {t('notifications:settings.messages.permissionDenied')}
              </p>
              <ol>
                <li>{t('notifications:settings.messages.clickLockIcon')}</li>
                <li>{t('notifications:settings.messages.changeNotifications')}</li>
                <li>{t('notifications:settings.messages.refreshPage')}</li>
              </ol>
            </div>
          )}

          {canSubscribe && !isSubscribed && (
            <Button 
              onClick={subscribe} 
              disabled={isLoading}
              variant="primary"
              fullWidth
            >
              {isLoading ? t('notifications:settings.actions.subscribing') : t('notifications:settings.actions.subscribe')}
            </Button>
          )}

          {isSubscribed && (
            <>
              <Button 
                onClick={testNotification} 
                disabled={isLoading}
                variant="secondary"
                fullWidth
              >
                {isLoading ? t('notifications:settings.actions.sending') : t('notifications:settings.actions.sendTestNotification')}
              </Button>
              
              <Button 
                onClick={handleTriggerScheduled} 
                disabled={isTriggeringScheduled}
                variant="secondary"
                fullWidth
              >
                {isTriggeringScheduled ? t('notifications:settings.actions.triggering') : t('notifications:settings.actions.triggerScheduled')}
              </Button>
              
              <Button 
                onClick={handleTestServiceWorker} 
                disabled={isTestingServiceWorker}
                variant="secondary"
                fullWidth
              >
                {isTestingServiceWorker ? t('notifications:settings.actions.testing') : t('notifications:settings.actions.testServiceWorker')}
              </Button>

              <Button 
                onClick={unsubscribe} 
                disabled={isLoading}
                variant="destructive"
                fullWidth
              >
                {isLoading ? t('notifications:settings.actions.unsubscribing') : t('notifications:settings.actions.unsubscribe')}
              </Button>
            </>
          )}
        </div>

        {/* Information */}
        <div className="notification-info">
          <h4>{t('notifications:settings.info.title')}</h4>
          <ul>
            <li>📅 <strong>{t('notifications:settings.info.monday8am')}:</strong> {t('notifications:settings.info.weeklyReminders')}</li>
            <li>🌅 <strong>{t('notifications:settings.info.daily8am')}:</strong> {t('notifications:settings.info.choresDueToday')}</li>
            <li>⏰ <strong>{t('notifications:settings.info.specificTimes')}:</strong> {t('notifications:settings.info.customNotificationTimes')}</li>
          </ul>
          
          <p className="note">
            <strong>{t('notifications:settings.info.note')}:</strong> {t('notifications:settings.info.workWhenClosed')}
          </p>
        </div>
      </div>
    </Card>
  );
};

export default NotificationSettings;
